import { Fragment, type ReactNode } from 'react';

/**
 * One catalogue sentence with marked-up islands dropped into its `{slot}`s.
 *
 * Where a slot sits in the sentence is the translation's to decide; what goes in it — an
 * identifier in `.mono`, a bolded phrase, a link — is markup, which does not belong in a
 * JSON string. Splitting a sentence into `…note.a` + `<strong>` + `…note.b` instead would
 * put English word order into the JSX, where no translator can move it.
 *
 * Nothing is parsed and no HTML is interpreted: the text around the slots becomes text
 * nodes, so a catalogue value can never introduce markup. A slot the text does not contain
 * is simply not rendered, and a slot the caller does not supply is left as literal `{name}`
 * — loud in the UI, harmless at run time. `i18n.catalogue.test.ts` fails the build before
 * either reaches anyone.
 *
 * Two shapes, because most call sites have exactly one island:
 *   <Interpolated text={t('shell.error.cookie')} slot="{keys}"><span …/></Interpolated>
 *   <Interpolated text={t('login.hint')} parts={{ '{a}': <code>…</code>, '{b}': <b>…</b> }} />
 */
/**
 * The same thing as a plain call, for a sentence that already sits inside JSX:
 * `{fill(t('settings.no-credential.note'), { header: <code>x-admin-password</code> })}`.
 */
export function fill(text: string, parts: Record<string, ReactNode>): ReactNode[] {
  return text.split(/(\{\w+\})/).map((piece, i) => {
    // Either spelling of a slot name is accepted — `{ version: … }` and `{ '{version}': … }`
    // read equally well at a call site, and a silent mismatch would print a raw `{version}`
    // on the screen rather than fail anything.
    const bare = /^\{(\w+)\}$/.exec(piece)?.[1];
    const key = piece in parts ? piece : bare !== undefined && bare in parts ? bare : null;
    return key === null ? piece : <Fragment key={i}>{parts[key]}</Fragment>;
  });
}

export function Interpolated({
  text,
  slot,
  parts,
  children,
}: {
  text: string;
  /** The single slot `children` fills, e.g. `"{version}"`. */
  slot?: string;
  /** Several slots at once, keyed by the literal slot text. */
  parts?: Record<string, ReactNode>;
  children?: ReactNode;
}) {
  const islands: Record<string, ReactNode> = { ...parts, ...(slot ? { [slot]: children } : {}) };
  return <>{fill(text, islands)}</>;
}
